import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Upload, Inbox, Network, FileText, RefreshCw,
} from 'lucide-react';
import { analystApi, listContentItems, listReports, getInboxCounts, type AnalystSummary } from '../services/api';
import { AnalystWidget } from '../components/analyst/AnalystWidget';
import type { ContentItem, GeneratedArticle, InboxCounts } from '../types/v2';

export function DashboardPage() {
  const navigate = useNavigate();

  const [items, setItems]           = useState<ContentItem[]>([]);
  const [reports, setReports]       = useState<GeneratedArticle[]>([]);
  const [counts, setCounts]         = useState<InboxCounts>({ pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading]       = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [analyst, setAnalyst]       = useState<AnalystSummary | null>(null);
  const [analystError, setAnalystError] = useState<string | null>(null);

  const loadAnalyst = useCallback(async () => {
    try {
      setAnalyst(await analystApi.summary());
      setAnalystError(null);
    } catch (err) {
      setAnalystError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const [itemsData, reportsData, countsData] = await Promise.all([
        listContentItems(),
        listReports(),
        getInboxCounts(),
      ]);
      setItems(itemsData);
      setReports(reportsData.slice(0, 5));
      setCounts(countsData);
      setLastRefresh(new Date());
    } catch {
      // silently ignore — items may be empty
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    void loadAnalyst();
    const interval = setInterval(() => { load(); void loadAnalyst(); }, 30_000);
    return () => clearInterval(interval);
  }, [load, loadAnalyst]);

  const inPipeline = items.filter((i) => i.pipeline_status !== 'GRAPH_SYNCED' && i.pipeline_status !== 'ERROR');
  const synced     = items.filter((i) => i.pipeline_status === 'GRAPH_SYNCED');
  const byType     = items.reduce<Record<string, number>>((acc, i) => {
    acc[i.source_type] = (acc[i.source_type] ?? 0) + 1;
    return acc;
  }, {});

  const SOURCE_LABELS: Record<string, string> = {
    upload_document: 'Documenti',
    news_api:        'Notizie',
    web_scraping:    'Web',
    rss_feed:        'RSS',
    social_api:      'Social',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        <RefreshCw className="w-5 h-5 animate-spin mr-2" />
        Caricamento...
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <LayoutDashboard className="w-6 h-6 text-blue-400" />
          <h2 className="text-xl font-semibold">Dashboard</h2>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white transition-colors"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Aggiorna
        </button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Fonti totali"     value={items.length}      color="blue" />
        <StatCard label="In pipeline"      value={inPipeline.length} color="orange" />
        <StatCard label="Nel grafo"        value={synced.length}     color="green" />
        <StatCard label="Articoli gen."    value={reports.length}    color="purple" />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <StatCard label="Analisi in corso"     value={analyst?.running ?? 0}      color="blue" />
        <StatCard label="Analisi in attesa di risposta" value={analyst?.waitingInput ?? 0} color="orange" />
      </div>
      {analystError && <p className="text-xs text-red-300">Agente Analista non disponibile: {analystError}</p>}
      <AnalystWidget summary={analyst} onStarted={() => { void loadAnalyst(); }} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Knowledge base */}
        <section className="bg-gray-900 border border-gray-800 rounded-xl p-5 space-y-3">
          <h3 className="text-sm font-semibold text-gray-300">Knowledge Base</h3>
          {Object.entries(byType).length === 0 ? (
            <p className="text-xs text-gray-500">Nessuna fonte ancora caricata.</p>
          ) : (
            <ul className="space-y-1.5">
              {Object.entries(byType).map(([type, count]) => (
                <li key={type} className="flex justify-between text-sm">
                  <span className="text-gray-400">{SOURCE_LABELS[type] ?? type}</span>
                  <span className="font-semibold text-white">{count}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Inbox pending */}
        <section
          className={`border rounded-xl p-5 space-y-3 ${
            counts.pending > 0
              ? 'bg-orange-950/30 border-orange-800'
              : 'bg-gray-900 border-gray-800'
          }`}
        >
          <div className="flex items-center gap-2">
            <Inbox className={`w-4 h-4 ${counts.pending > 0 ? 'text-orange-400' : 'text-gray-500'}`} />
            <h3 className="text-sm font-semibold text-gray-300">Inbox — In attesa di review</h3>
          </div>
          {counts.pending > 0 ? (
            <>
              <p className="text-3xl font-bold text-orange-400">{counts.pending}</p>
              <p className="text-xs text-gray-400">
                {counts.approved} approvati · {counts.rejected} rifiutati
              </p>
              <button
                onClick={() => navigate('/inbox')}
                className="w-full bg-orange-600 hover:bg-orange-500 text-white text-sm py-2 rounded-lg font-medium transition-colors"
              >
                Vai all&apos;Inbox →
              </button>
            </>
          ) : (
            <p className="text-xs text-gray-500">Nessun elemento in attesa di review.</p>
          )}
        </section>
      </div>

      {/* Pipeline in corso */}
      {inPipeline.length > 0 && (
        <section className="bg-gray-900 border border-gray-800 rounded-xl p-5 space-y-3">
          <h3 className="text-sm font-semibold text-gray-300">Pipeline in corso ({inPipeline.length})</h3>
          <ul className="space-y-1.5">
            {inPipeline.slice(0, 5).map((item) => (
              <li key={item.id} className="flex items-center justify-between text-xs">
                <span className="text-gray-300 truncate max-w-xs">{item.title || item.id.slice(0, 12)}</span>
                <span className="text-yellow-400 font-mono">{item.pipeline_status}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Quick actions */}
      <section className="bg-gray-900 border border-gray-800 rounded-xl p-5 space-y-3">
        <h3 className="text-sm font-semibold text-gray-300">Azioni rapide</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <QuickAction icon={<Upload className="w-5 h-5" />}   label="Carica documento" onClick={() => navigate('/ingest')} />
          <QuickAction icon={<Inbox className="w-5 h-5" />}    label="Vai all'Inbox"    onClick={() => navigate('/inbox')} />
          <QuickAction icon={<Network className="w-5 h-5" />}  label="Esplora grafo"    onClick={() => navigate('/graph')} />
          <QuickAction icon={<FileText className="w-5 h-5" />} label="Genera articolo"  onClick={() => navigate('/report')} />
        </div>
      </section>

      {/* Ultimi articoli */}
      {reports.length > 0 && (
        <section className="bg-gray-900 border border-gray-800 rounded-xl p-5 space-y-3">
          <h3 className="text-sm font-semibold text-gray-300">Ultimi articoli generati</h3>
          <ul className="space-y-2">
            {reports.map((r) => (
              <li
                key={r.id}
                className="flex items-center justify-between text-sm hover:bg-gray-800 rounded-lg px-2 py-1.5 cursor-pointer"
                onClick={() => navigate(`/publish/${r.id}`)}
              >
                <span className="text-gray-300 truncate max-w-sm">{r.title}</span>
                <span className={`text-xs font-medium ${r.published ? 'text-green-400' : 'text-gray-500'}`}>
                  {r.published ? 'Pubblicato' : 'Bozza'}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <p className="text-xs text-gray-600 text-right">
        Aggiornato alle {lastRefresh.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
      </p>
    </div>
  );
}

function StatCard({ label, value, color }: { label: string; value: number; color: 'blue' | 'orange' | 'green' | 'purple' }) {
  const colorMap = {
    blue:   'text-blue-400',
    orange: 'text-orange-400',
    green:  'text-green-400',
    purple: 'text-purple-400',
  };
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
      <p className={`text-2xl font-bold ${colorMap[color]}`}>{value}</p>
      <p className="text-xs text-gray-500 mt-0.5">{label}</p>
    </div>
  );
}

function QuickAction({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center gap-2 p-4 rounded-lg border border-gray-800 hover:border-blue-700 hover:bg-blue-900/20 text-gray-400 hover:text-white transition-colors text-xs text-center"
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}
